// Constants and response types with no runtime dependencies (safe for the browser bundle)
// ---------- Enums ----------
export const ACCOUNT_KINDS = ['shop', 'fleet', 'owner_operator'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

/** Packages a company can buy; any combination (a carrier can run its own shop, fleet and owner-operators) */
export const MODULES = ACCOUNT_KINDS;
export type Module = AccountKind;
/** owner_operator = an owner-operator leased on to the company: sees only his own trucks */
export const ROLES = ['admin', 'advisor', 'mechanic', 'driver', 'owner_operator'] as const;
export type Role = (typeof ROLES)[number];

export const WO_STATUSES = ['estimate', 'approved', 'in_progress', 'waiting_parts', 'completed', 'invoiced', 'paid'] as const;
export type WoStatus = (typeof WO_STATUSES)[number];
export const LOCKED_STATUSES: readonly WoStatus[] = ['invoiced', 'paid'];
export const FINISHED_STATUSES: readonly WoStatus[] = ['completed', 'invoiced', 'paid'];
export const MECHANIC_STATUSES: readonly WoStatus[] = ['in_progress', 'waiting_parts', 'completed'];

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const UNIT_KINDS = ['truck', 'trailer', 'other'] as const;
export const MAINTENANCE_CATEGORIES = ['pm', 'repair', 'inspection', 'tire', 'dot_annual', 'other'] as const;
export const STOCK_REASONS = ['receive', 'adjust', 'return'] as const;
export const PM_STATES = ['ok', 'due_soon', 'overdue'] as const;
export type PmState = (typeof PM_STATES)[number];

export const AI_JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export type AiJobStatus = (typeof AI_JOB_STATUSES)[number];
export const URGENCY = ['safe_to_drive', 'drive_with_caution', 'do_not_drive'] as const;
export const LEVELS = ['high', 'medium', 'low'] as const;
export const OUTCOMES = ['fixed', 'partially_fixed', 'not_fixed'] as const;

// Itemized maintenance (receipts, work orders)
export const ITEM_KINDS = ['part', 'labor', 'tire', 'fluid', 'fee', 'other'] as const;
export const SYSTEM_CATEGORIES = [
  'brakes', 'engine', 'aftertreatment', 'cooling', 'fuel', 'electrical', 'lighting', 'tires', 'wheels_hubs',
  'suspension', 'steering', 'transmission', 'drivetrain', 'clutch', 'air_system', 'hvac', 'cab_body',
  'trailer', 'reefer', 'pm_service', 'inspection', 'other'
] as const;
export type SystemCategory = (typeof SYSTEM_CATEGORIES)[number];
export const RECEIPT_STATUSES = ['processing', 'review', 'saved', 'failed'] as const;
export const FILE_KINDS = ['receipt', 'unit_photo', 'inspection', 'dvir', 'payment', 'other'] as const;
// Ways a shop takes payment at the counter. Fleet checks are paid with an express code from the carrier.
export const FLEET_CHECKS = ['efs', 'comchek', 'tchek', 'fleet_check'] as const;
export const PAY_METHODS = ['cash', 'card', 'check', 'ach', 'other', ...FLEET_CHECKS] as const;
export const OWNERSHIP = ['owned', 'leased', 'rented', 'owner_operator'] as const;

// FMCSA Part 396
export const INSPECTION_KINDS = ['annual', 'roadside', 'brake', 'other'] as const;
export const INSPECTION_RESULTS = ['pass', 'fail', 'out_of_service'] as const;
/** §396.11(a)(1): parts and accessories the driver must cover in the DVIR */
export const DVIR_ITEMS = [
  'service_brakes', 'parking_brake', 'steering', 'lighting_reflectors', 'tires', 'horn',
  'windshield_wipers', 'mirrors', 'coupling_devices', 'wheels_rims', 'emergency_equipment'
] as const;
/**
 * The trailer's part of the DVIR: the §396.11(a)(1) items that exist on a trailer (service brakes including trailer
 * brake connections, parking/spring brakes, lighting and reflectors, tires, coupling devices, wheels and rims)
 * plus the trailer parts drivers are expected to check on a walk-around.
 */
export const TRAILER_DVIR_ITEMS = [
  'service_brakes', 'brake_connections', 'parking_brake', 'lighting_reflectors', 'tires', 'wheels_rims', 'coupling_devices',
  'landing_gear', 'suspension', 'body_doors', 'conspicuity_tape'
] as const;
export const ALL_DVIR_ITEMS = [...new Set([...DVIR_ITEMS, ...TRAILER_DVIR_ITEMS])] as unknown as readonly [DvirItem, ...DvirItem[]];
export type DvirItem = (typeof DVIR_ITEMS)[number] | (typeof TRAILER_DVIR_ITEMS)[number];
export const dvirItemsFor = (kind: string): readonly DvirItem[] => (kind === 'trailer' ? TRAILER_DVIR_ITEMS : DVIR_ITEMS);
/** English names for DVIR items (the API writes them into the defects text kept in the record) */
export const DVIR_LABELS: Record<DvirItem, string> = {
  service_brakes: 'Service brakes', parking_brake: 'Parking brake', steering: 'Steering', lighting_reflectors: 'Lighting / reflectors',
  tires: 'Tires', horn: 'Horn', windshield_wipers: 'Windshield wipers', mirrors: 'Mirrors', coupling_devices: 'Coupling devices',
  wheels_rims: 'Wheels / rims', emergency_equipment: 'Emergency equipment',
  brake_connections: 'Air lines / gladhands / brake connections', landing_gear: 'Landing gear', suspension: 'Suspension / air bags',
  body_doors: 'Body / doors / frame', conspicuity_tape: 'Reflective (conspicuity) tape'
};
export const TRAILER_TYPES = ['dry_van', 'reefer', 'flatbed', 'step_deck', 'lowboy', 'tanker', 'container_chassis', 'dump', 'car_hauler', 'other'] as const;
export const RETENTION = {
  maintenance_months: 12,      // §396.3(c)
  after_unit_left_months: 6,   // §396.3(c)
  dvir_months: 3,              // §396.11(a)(4)
  annual_inspection_months: 14,// §396.21(b)
  roadside_months: 12,         // §396.9(d)(3)
  roadside_certify_days: 15,   // §396.9(d)(3)
  annual_interval_months: 12   // §396.17
} as const;

export const PARTS_REQUEST_STATUSES = ['requested', 'quoted', 'approved', 'ordered', 'shipped', 'delivered', 'cancelled'] as const;
export type PartsRequestStatus = (typeof PARTS_REQUEST_STATUSES)[number];
export const PARTS_URGENCY = ['normal', 'urgent', 'truck_down'] as const;
export const SHIP_TO = ['shop', 'truck_location', 'other'] as const;


// AI result shapes (also used as JSON schema for the model; see api/src/ai)
export interface DiagnosisResult {
  summary: string;
  urgency: (typeof URGENCY)[number];
  out_of_service_risk: boolean;
  confidence: (typeof LEVELS)[number];
  likely_causes: { cause: string; probability: (typeof LEVELS)[number]; reasoning: string }[];
  diagnostic_steps: { step: string; tools: string; expected_result: string }[];
  repair_plan: { title: string; description: string; labor_hours: number; steps: string[]; specs_to_verify: string[] }[];
  parts: {
    name: string; part_number: string; alt_part_numbers: string[]; brand: string; quantity: number;
    needed_for: string; part_number_confidence: (typeof LEVELS)[number]; notes: string;
  }[];
  questions_for_driver: string[];
  safety_notes: string[];
}
export interface PartOffer {
  part_number: string; part_name: string; vendor: string; url: string; price: number; price_known: boolean;
  currency: string; availability: string; condition: 'new' | 'remanufactured' | 'used' | 'unknown'; notes: string;
}
export interface SourcingResult { notes: string; offers: PartOffer[]; searched_at: string }

// ---------- Common response types ----------
export interface Page<T> { items: T[]; next_cursor: number | null }
export interface ApiErrorBody { error: string; details?: unknown; request_id?: string }


/** UI languages. Add a language: put its code here and add apps/web/src/locales/<code>.json */
export const LANGUAGES = [
  { code: 'en', name: 'English', ai: 'English' },
  { code: 'ru', name: 'Русский', ai: 'Russian' },
  { code: 'uz', name: "O'zbekcha", ai: 'Uzbek (Latin script)' },
  { code: 'tr', name: 'Türkçe', ai: 'Turkish' },
  { code: 'es', name: 'Español', ai: 'Spanish' },
  { code: 'uk', name: 'Українська', ai: 'Ukrainian' },
  { code: 'tg', name: 'Тоҷикӣ', ai: 'Tajik (Cyrillic script)' },
  { code: 'so', name: 'Soomaali', ai: 'Somali' },
  { code: 'ar', name: 'العربية', ai: 'Arabic' }
] as const;
export type LanguageCode = (typeof LANGUAGES)[number]['code'];
/** Languages written right to left */
export const RTL_LANGUAGES = ['ar'] as const;
export const isRtl = (code: string | null | undefined) => code === 'ar';
export const languageName = (code: string | null | undefined) => LANGUAGES.find(l => l.code === code)?.ai ?? 'English';

/**
 * Standard PM schedules a fleet can add to a vehicle in one click (names are stored as written: DOT records are kept in English).
 * Whichever interval comes first makes it due. Annual DOT inspection (§396.17) is tracked separately in DOT compliance.
 */
export const PM_TEMPLATES: Record<'truck' | 'trailer' | 'reefer', { name: string; interval_miles?: number; interval_days?: number; interval_hours?: number }[]> = {
  truck: [
    { name: 'PM-A: Engine oil & filters', interval_miles: 25000, interval_days: 90 },
    { name: 'PM-B: Chassis lube & full inspection', interval_miles: 50000, interval_days: 180 },
    { name: 'Air dryer cartridge', interval_miles: 100000, interval_days: 365 },
    { name: 'Coolant test & DEF filter', interval_miles: 100000, interval_days: 365 }
  ],
  trailer: [
    { name: 'Trailer PM: brakes, air lines, lights, tires, coupler', interval_miles: 25000, interval_days: 90 },
    { name: 'Wheel ends: hub oil level & seals', interval_days: 90 },
    { name: 'Brake adjustment & lining check', interval_miles: 25000, interval_days: 90 },
    { name: 'Landing gear, kingpin & upper coupler lube', interval_days: 90 },
    { name: 'Wheel bearing service', interval_miles: 100000, interval_days: 365 }
  ],
  reefer: [
    { name: 'Reefer unit: oil, filters & belts', interval_hours: 1500, interval_days: 365 },
    { name: 'Reefer pre-trip / temperature check', interval_days: 30 }
  ]
};

/** How the app looks, picked per person in Settings */
export const UI_THEMES = ['garage', 'night', 'office'] as const;

// Accounting: categories people pick for money VegaPart does not count on its own
export const LEDGER_INCOME = ['freight', 'fuel_surcharge', 'detention', 'service_other', 'other_income'] as const;
export const LEDGER_EXPENSE = ['fuel', 'payroll', 'driver_pay', 'truck_payment', 'insurance', 'rent', 'utilities', 'tolls', 'permits',
  'parts_purchase', 'tools', 'software', 'office', 'taxes_fees', 'other_expense'] as const;
export const LEDGER_KINDS = ['income', 'expense'] as const;

// ---------- Digital vehicle inspection (shop's walk-around on a work order) ----------
export const DVI_STATUSES = ['ok', 'soon', 'now'] as const;          // green, yellow, red
export type DviStatus = (typeof DVI_STATUSES)[number];
export const DVI_TRUCK_ITEMS = [
  'tires', 'brakes', 'brake_chambers', 'air_system', 'steering', 'suspension', 'wheel_ends', 'lights', 'wipers_glass',
  'battery_charging', 'belts_hoses', 'coolant', 'oil_leaks', 'fluids', 'exhaust_dpf', 'fifth_wheel', 'driveline', 'frame', 'mirrors_cab'
] as const;
export const DVI_TRAILER_ITEMS = [
  'tires', 'brakes', 'brake_chambers', 'air_lines', 'suspension', 'wheel_ends', 'lights', 'landing_gear', 'kingpin',
  'doors', 'floor_walls', 'frame', 'mud_flaps', 'reflective_tape'
] as const;

// ---------- Appointments ----------
export const APPOINTMENT_STATUSES = ['scheduled', 'arrived', 'no_show', 'cancelled'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];
export const CONTACT_KINDS = ['pm_reminder', 'appointment', 'appointment_reminder', 'call', 'note'] as const;
